import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";

import "flatpickr/dist/plugins/monthSelect/style.css";

import { FocusEvent, useCallback, useMemo, useRef } from "react";
import { Instance } from "flatpickr/dist/types/instance";
import { Options } from "flatpickr/dist/types/options";

import { DateTimePickerMarkup } from "./markup";
import { InternalDatePickerProps } from "./types";

export function DateTimePicker(props: InternalDatePickerProps) {
	const {
		format,
		enableTime,
		enableDate,
		dateOnly,
		minDate,
		maxDate,
		minTime,
		maxTime,
		minuteIncrement,
		allowInput = true,
		enabledDates,
		showMonths,
		monthsOnly,
		...rest
	} = props;
	const instanceRef = useRef<Instance>();

	/**
	 * On change handler. Will call the change handler that is provided,
	 * and if the onChange is not undefined, will also call that
	 * @param selectedDates: Date selected
	 * @param dateStr: Stringified date selected
	 * @param instance: Instance of the date time picker
	 */
	const change = useCallback(
		function (selectedDates: Date[], dateStr: string, instance: Instance) {
			if (!selectedDates?.length) selectedDates = [];
			if (props.changeHandler !== undefined) props.changeHandler(selectedDates[0], props.id);
			if (props.onChange !== undefined) {
				props.onChange(selectedDates, dateStr, instance);
			}
		},
		[props],
	);

	const blur = (event: FocusEvent<HTMLInputElement>) => {
		event.preventDefault();

		if (event.target.value === "" || monthsOnly) return;
		if (typeof props.value === "number") return;

		const date = tryParseDate(event.target.value);
		const value = tryParseDate(props.value);

		if (!date) return;
		if (date.toISOString() === value?.toISOString()) return;

		change([date], event.target.value, null);
	};

	const onOpen = useCallback(function (selectedDates: Date[], dateStr: string, instance: Instance) {
		instanceRef.current = instance;
		const enabledDates = instance.config.enable?.map((d) => new Date(d.toString()));
		if (enabledDates?.length) {
			const enabledStrings = enabledDates?.map((d) => d.toDateString());
			const selectedStrings = selectedDates?.map((s) => s.toDateString());
			//Switch month if no selected dates, or if no selected dates are in enabled list
			if (!selectedStrings.some((e) => enabledStrings.includes(e)))
				instance.changeMonth(enabledDates[0].getMonth(), false);
		}
	}, []);

	const dateFormat = useMemo(() => {
		const formatObj = new Intl.DateTimeFormat(window.navigator.language).formatToParts(new Date());
		return formatObj
			.map((obj) => {
				switch (obj.type) {
					case "day":
						return obj.value.length < 2 ? "j" : "d";
					case "month":
						return obj.value.length < 2 ? "n" : "m";
					case "year":
						return obj.value.length < 2 ? "y" : "Y";
					default:
						return obj.value;
				}
			})
			.join(``);
	}, []);

	function tryParseDate(dateString: string | Date): Date | null {
		if (typeof dateString === "object") return dateString;

		try {
			const splitDate = dateString.split(/[.\-/]/);
			const currentYear = new Date().getFullYear();
			if (splitDate[2]?.length === 2) {
				let year = parseInt(splitDate[2]) + 2000;
				if (year > currentYear) year -= 100;
				const month = parseInt(splitDate[0]) - 1;
				const day = parseInt(splitDate[1]);
				return new Date(year, month, day);
			} else {
				// We have to use the split date because safari only supports / format
				const year = parseInt(splitDate[2]);
				const month = parseInt(splitDate[0]) - 1;
				const day = parseInt(splitDate[1]);

				return new Date(year, month, day);
			}
		} catch (e) {
			return null;
		}
	}

	const options = useMemo(() => {
		const datePickerOptions: Options = {
			dateFormat: `${dateFormat} h:i K`,
			enableTime: true,
			//MSC 1/19/24 - For whatever reason, mobile view won't undisable if it starts disabled.  So we have to drop the mobile view
			disableMobile: true,

			parseDate: tryParseDate,
		};
		if (dateOnly || enableTime === false) {
			datePickerOptions.dateFormat = dateFormat;
			datePickerOptions.enableTime = false;
		}
		if (format !== undefined) {
			datePickerOptions.dateFormat = format;
		}
		if (enableDate !== undefined) {
			datePickerOptions.noCalendar = !enableDate;
		}
		if (minTime !== undefined) {
			datePickerOptions.minTime = minTime;
		}
		if (maxTime !== undefined) {
			datePickerOptions.maxTime = maxTime;
		}
		if (minDate !== undefined) {
			datePickerOptions.minDate = minDate;
		}
		if (maxDate !== undefined) {
			datePickerOptions.maxDate = maxDate;
		}
		if (minuteIncrement !== undefined) {
			datePickerOptions.minuteIncrement = minuteIncrement;
		}
		if (allowInput !== undefined) {
			datePickerOptions.allowInput = allowInput;
		}
		if (enabledDates !== undefined) {
			datePickerOptions.enable = enabledDates;
		}
		if (showMonths !== undefined) {
			datePickerOptions.showMonths = showMonths;
		}
		if (monthsOnly === true) {
			datePickerOptions.plugins = [monthSelectPlugin({ shorthand: true })];
		}
		return datePickerOptions;
	}, [
		dateFormat,
		enableDate,
		enableTime,
		dateOnly,
		format,
		minTime,
		maxTime,
		minDate,
		maxDate,
		minuteIncrement,
		allowInput,
		enabledDates,
		showMonths,
		monthsOnly,
	]);

	// The date is stored as a string sometimes, so we need to convert it back to a Date object
	const value = useMemo(() => {
		if (!props.value) return null;
		if (typeof props.value === "string") return new Date(props.value);
		return props.value;
	}, [props.value]);

	return (
		<DateTimePickerMarkup
			{...rest}
			value={value}
			onChange={change}
			onOpen={onOpen}
			onUnfocused={blur}
			datePickerOptions={options}
		/>
	);
}

export * from "./types";
